import { stateOptions, termOptions } from "./options";

export function slugify(str) {
  return str
    ?.toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function unSlugify(str) {
  return str
    .split("-") // Split based on hyphens
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(" "); // Join the words with a space
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatDateString(dateLike) {
  if (!dateLike) return "";

  // 1) Date instance (from valueAsDate)
  // Use UTC accessors so "picked date" is preserved regardless of timezone.
  if (dateLike instanceof Date && !isNaN(dateLike)) {
    const y = dateLike.getUTCFullYear();
    const m = pad2(dateLike.getUTCMonth() + 1);
    const d = pad2(dateLike.getUTCDate());
    return `${y}-${m}-${d}`; // YYYY-MM-DD
  }

  // 2) Already "YYYY-MM-DD"
  if (typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
    return dateLike;
  }

  // 3) "MM/DD/YYYY" → normalize to "YYYY-MM-DD"
  if (
    typeof dateLike === "string" &&
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(dateLike)
  ) {
    const [, mm, dd, yyyy] = dateLike.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }

  // 4) Fallback: parse anything else Date can understand, still using UTC
  const d = new Date(dateLike);
  if (!isNaN(d)) {
    const y = d.getUTCFullYear();
    const m = pad2(d.getUTCMonth() + 1);
    const day = pad2(d.getUTCDate());
    return `${y}-${m}-${day}`;
  }

  // 5) Give up gracefully
  return "";
}

export function getStateObject(abbreviation) {
  return stateOptions.find((state) => state.value === abbreviation) || null;
}

export function getTermObject(label) {
  return termOptions.find((term) => term.label === label) || null;
}

export function extractSlugPart(source) {
  // A) If given a string (e.g., pathname like "/review-foo-bar-abc123")
  if (typeof source === "string") {
    // Parse common patterns: /review-<slug>-<id>, /edit-<slug>-<id>, or "<slug>-<id>"
    const m =
      source.match(
        /(?:^|\/)(?:review|edit)-(.+?)-([A-Za-z0-9]+)(?:$|[/?#])/i
      ) || source.match(/^(.+?)-([A-Za-z0-9]+)$/);

    if (m) {
      return m[1]; // the <slug> portion (company/common/service name)
    }

    // Fallback: take last non-empty path segment and strip a trailing "-<id>" shape if present
    const parts = source.split("/").filter(Boolean);
    const last = parts[parts.length - 1] || "";
    const m2 = last.match(/^(.+?)-([A-Za-z0-9]+)$/);
    return m2 ? m2[1] : last;
  }

  // B) If given an object (exhibit), build a slug from its naming fields
  if (source && typeof source === "object") {
    const base =
      source.companyName ||
      source.commonName ||
      source.serviceProvider ||
      "review";
    return slugify(base);
  }

  // C) Fallback
  return "review";
}

/**
 * Build the path and navigation state for Review.
 * fromSaveContinue=true tells Review to render the submit section.
 */
export function buildReviewURL(exhibit, fromSaveContinue = false) {
  const id = exhibit?.id || exhibit?.docId || exhibit?.exhibitId || ""; // must exist for stable deep-link

  const slug = extractSlugPart(exhibit); // works with object or string
  const path = `/review-${slug}-${slugify(String(id))}`;

  return {
    path,
    state: {
      exhibitId: id,
      fromSaveContinue,
    },
  };
}
